import { maintenanceScopeLabel } from './utils/maintenanceScope';

const DAY_MS = 24 * 60 * 60 * 1000;

// Inspection terms are calendar dates; UTC arithmetic keeps DST from shifting them.
function calendarDate(value) {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4}-\d{2}-\d{2})(?:T.*)?$/.exec(value.trim());
  if (!match) return null;
  const iso = match[1];
  const timestamp = Date.parse(`${iso}T00:00:00.000Z`);
  if (!Number.isFinite(timestamp) || new Date(timestamp).toISOString().slice(0, 10) !== iso) {
    return null;
  }
  return { iso, timestamp };
}

export function torontoDateISO(now = new Date()) {
  const date = now instanceof Date ? now : new Date(now);
  if (!Number.isFinite(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function getOverviewWeek(today) {
  const reference = calendarDate(today);
  if (!reference) return { startDate: '', endDate: '' };
  const daysSinceMonday = (new Date(reference.timestamp).getUTCDay() + 6) % 7;
  const monday = reference.timestamp - daysSinceMonday * DAY_MS;
  return {
    startDate: new Date(monday).toISOString().slice(0, 10),
    endDate: new Date(monday + 6 * DAY_MS).toISOString().slice(0, 10),
  };
}

function inspectionKey(building, inspection) {
  if (inspection.objectId) return `inspection:${inspection.objectId}`;
  return `cycle:${JSON.stringify([
    building.buildingId || building.objectId || '',
    inspection.maintenanceType || building.contractType || '',
    calendarDate(inspection.startTerm)?.iso || inspection.startTerm || '',
    calendarDate(inspection.endTerm)?.iso || inspection.endTerm || '',
  ])}`;
}

function statusKey(status) {
  return String(status || '').trim().toLowerCase();
}

function isCancelled(status) {
  return ['cancelled', 'canceled'].includes(statusKey(status));
}

function overlapsWeek(start, end, week) {
  return Boolean(start && end && week.startDate
    && start.iso <= end.iso && start.iso <= week.endDate && end.iso >= week.startDate);
}

function sortVisits(visits) {
  return visits.sort((a, b) => a.startDate.localeCompare(b.startDate)
    || a.buildingName.localeCompare(b.buildingName)
    || a.key.localeCompare(b.key));
}

export function summarizeOverviewSchedule(buildings, today) {
  const week = getOverviewWeek(today);
  const reference = calendarDate(today);
  const seen = new Set();
  const closeoutKeys = new Set();
  const inSuiteVisits = [];

  (Array.isArray(buildings) ? buildings : []).forEach((building) => {
    if (!building || typeof building !== 'object') return;
    // One inspection may appear on multiple contract rows and in both collections.
    const inspections = [
      ...(Array.isArray(building.maintenanceHistory) ? building.maintenanceHistory : []),
      ...(building.upcomingInspection ? [building.upcomingInspection] : []),
    ];
    inspections.forEach((inspection) => {
      if (!inspection || typeof inspection !== 'object' || isCancelled(inspection.status)) return;
      // Today excludes Project cycles; the in-suite overview keeps the source data.
      if (String(inspection.cycle || '').trim().toLowerCase() === 'project') return;
      const key = inspectionKey(building, inspection);
      if (seen.has(key)) return;
      seen.add(key);
      const start = calendarDate(inspection.startTerm);
      const end = calendarDate(inspection.endTerm);
      const hasStart = inspection.startTerm != null && inspection.startTerm !== '';
      const hasEnd = inspection.endTerm != null && inspection.endTerm !== '';

      // This is a review queue, not proof that field work is complete or uninvoiced.
      // Never substitute startTerm for a missing endTerm when flagging closeout.
      if (reference && statusKey(inspection.status) === 'active' && end
        && (!hasStart || start)
        && (!start || end.iso >= start.iso)
        && end.iso < reference.iso) {
        closeoutKeys.add(key);
      }

      // Missing end dates are same-day visits; malformed supplied dates are excluded.
      const visitEnd = hasEnd ? end : start;
      if (!overlapsWeek(start, visitEnd, week)) return;
      const canonicalType = inspection.maintenanceType || building.contractType || '';
      const scopeLabel = maintenanceScopeLabel(inspection, building, canonicalType);
      inSuiteVisits.push({
        key,
        buildingName: building.name || building.address || '',
        // The canonical code remains the identity/routing source; the verified
        // contract scope is presentation metadata only.
        maintenanceType: canonicalType,
        ...(scopeLabel && scopeLabel !== canonicalType ? { scopeLabel } : {}),
        startDate: start.iso,
        endDate: visitEnd.iso,
        status: inspection.status || '',
      });
    });
  });

  return { week, inSuiteVisits: sortVisits(inSuiteVisits), closeoutCount: closeoutKeys.size };
}

function mechanicalDate(value) {
  if (typeof value !== 'string' || !calendarDate(value)) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return calendarDate(value);
  // Jobber supplies timestamp offsets. Avoid interpreting timezone-less strings
  // in the viewer's device timezone.
  if (!/(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) return null;
  return calendarDate(torontoDateISO(value));
}

function groupMechanicalVisits(visits, range, calendar = false) {
  const seen = new Set();
  const weeklyVisits = [];
  const bookingGroups = new Map();
  (Array.isArray(visits) ? visits : []).forEach((visit) => {
    if (!visit || typeof visit !== 'object' || isCancelled(visit.status)) return;
    const start = mechanicalDate(visit.startAt);
    const hasEnd = visit.endAt != null && visit.endAt !== '';
    const end = hasEnd ? mechanicalDate(visit.endAt) : start;
    let overlapEnd = end;
    if (start && end && hasEnd && visit.startAt.includes('T') && visit.endAt.includes('T')) {
      const startTime = Date.parse(visit.startAt);
      const endTime = Date.parse(visit.endAt);
      if (endTime < startTime) return;
      // A visit ending exactly at Monday midnight belongs only to the prior week.
      if (endTime > startTime) overlapEnd = calendarDate(torontoDateISO(endTime - 1));
    }
    if (!start || !overlapEnd || start.iso > overlapEnd.iso) return;
    // Today retains its weekly-only grouping. A calendar groups the supplied
    // bookings first so one visit can continue across a week or month boundary.
    if (!calendar && !overlapsWeek(start, overlapEnd, range)) return;
    const buildingKey = visit.buildingObjectId || visit.buildingId || visit.buildingName || '';
    const key = visit.id ? `mechanical:${buildingKey}:${visit.id}` : `mechanical:${JSON.stringify([
      buildingKey, visit.title || '', visit.startAt, visit.endAt || '',
    ])}`;
    if (seen.has(key)) return;
    seen.add(key);
    const linkedBuilding = visit.buildingObjectId || visit.buildingId;
    if (String(linkedBuilding || '').trim() && String(visit.jobId || '').trim()) {
      bookingGroups.set(key, {
        groupKey: JSON.stringify([linkedBuilding, visit.jobId]),
        occupiedEnd: overlapEnd.iso,
      });
    }
    weeklyVisits.push({
      key,
      buildingName: visit.buildingName || '',
      ...(calendar ? {
        buildingId: visit.buildingId || visit.buildingObjectId || '',
        buildingObjectId: visit.buildingObjectId || '',
      } : {}),
      maintenanceType: visit.title || 'Mechanical maintenance',
      startDate: start.iso,
      // Calendar dates are inclusive occupied days; midnight is an exclusive end.
      endDate: calendar ? overlapEnd.iso : end.iso,
      status: visit.status || '',
    });
  });
  // Adjacent booked days on the same building's mechanical job are one visit.
  // Keep separate jobs, unlinked records, and later return visits distinct.
  const groupedVisits = [];
  const lastBookingByJob = new Map();
  const isCompleted = status => ['past', 'completed', 'complete'].includes(statusKey(status));
  sortVisits(weeklyVisits).forEach(visit => {
    const booking = bookingGroups.get(visit.key);
    const previous = booking && lastBookingByJob.get(booking.groupKey);
    if (previous && calendarDate(visit.startDate).timestamp <= calendarDate(previous.occupiedEnd).timestamp + DAY_MS) {
      previous.visit.endDate = previous.visit.endDate > visit.endDate ? previous.visit.endDate : visit.endDate;
      previous.visit.status = isCompleted(previous.visit.status) && isCompleted(visit.status) ? 'Completed' : 'Active';
      if (booking.occupiedEnd > previous.occupiedEnd) previous.occupiedEnd = booking.occupiedEnd;
    } else {
      groupedVisits.push(visit);
      if (booking) lastBookingByJob.set(booking.groupKey, { visit, occupiedEnd: booking.occupiedEnd });
    }
  });
  return calendar ? groupedVisits.filter(visit => overlapsWeek(
    calendarDate(visit.startDate), calendarDate(visit.endDate), range,
  )) : groupedVisits;
}

export function summarizeMechanicalVisits(visits, today) {
  return groupMechanicalVisits(visits, getOverviewWeek(today));
}

export function summarizeMechanicalVisitsInRange(visits, range) {
  const start = calendarDate(range?.startDate);
  const end = calendarDate(range?.endDate);
  if (!start || !end || start.iso > end.iso) return [];
  return groupMechanicalVisits(visits, { startDate: start.iso, endDate: end.iso }, true);
}

export function getVisitTiming(visit, today) {
  const reference = calendarDate(today);
  const start = calendarDate(visit?.startDate);
  const end = calendarDate(visit?.endDate) || start;
  if (!reference || !start || !end) return '';
  if (['past', 'completed', 'complete'].includes(statusKey(visit.status))) return 'Completed';
  const daysAway = (start.timestamp - reference.timestamp) / DAY_MS;
  if (daysAway === 0) return 'Starts today';
  if (daysAway === 1) return 'Tomorrow';
  if (daysAway > 1) return `In ${daysAway} days`;
  if (end.iso >= reference.iso) return 'In progress';
  const ended = new Intl.DateTimeFormat('en-CA', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  }).format(new Date(end.timestamp));
  return `Ended ${ended}`;
}
